import {
  API_FLOOR_ORDER,
  API_LOAN_ACTION,
  API_LOAN_ACTIONS,
  API_LOAN_AI_REVIEW_CONDITION,
  // API_LOAN_AI_REVIEW_CONDITION_PROMPT,
  API_LOAN_ANALYZE_DOCUMENT,
  API_LOAN_ANALYZE_DOCUMENT_HISTORY,
  API_LOAN_BORROWER_OUTSTANDING_ITEMS,
  API_LOAN_CLONE_FILE,
  API_LOAN_CLONE_GROUP,
  API_LOAN_COMPLIANCE,
  API_LOAN_COMPLIANCE_CONFIG,
  API_LOAN_CREATE_SAMPLE,
  API_LOAN_CUSTOM_INFORMATION,
  API_LOAN_DELETE,
  API_LOAN_DETAIL_LOG,
  API_LOAN_DOWNLOAD_ALL_CHANGES,
  API_LOAN_DOWNLOAD_MISMO_XML,
  API_LOAN_EXTRACT_DOCUMENT_BY_SECTIONS,
  API_LOAN_FETCH_FCI_DATA,
  API_LOAN_GET_ADDRESS_ROUTES,
  API_LOAN_GET_DUPLICATE_LOANS,
  API_LOAN_GET_GEOLOCATION,
  API_LOAN_GET_TIER_LEVEL_LIST,
  API_LOAN_GET_ZILLOW_URL,
  API_LOAN_GRID_CONFIG,
  API_LOAN_HISTORY,
  API_LOAN_LOG,
  API_LOAN_MLDS,
  API_LOAN_MOVE_TO_SERVICING,
  API_LOAN_NEXYS_PEERS,
  API_LOAN_NEXYS_SHARED_DOCUMENT,
  API_LOAN_NEXYS_SHARED_PAGE,
  API_LOAN_NEXYS_SHARED_PIPELINE,
  API_LOAN_NEXYS_SHARED_SHELL,
  API_LOAN_OVERVIEW,
  API_LOAN_OVERVIEW_OVERRIDES,
  API_LOAN_OVERVIEW_SECTION,
  API_LOAN_OVERVIEW_SECTION_BY_LOAN_NUMBER,
  API_LOAN_PIPELINE,
  API_LOAN_PIPELINE_VIEW_BY_ID,
  API_LOAN_PIPELINE_VIEWS,
  API_LOAN_PROCESS_LOG,
  API_LOAN_PUSH_TO_NEXYS,
  API_LOAN_PUSH_TO_NEXYS_LOCK_STATUS,
  API_LOAN_PUSH_TO_NEXYS_MERGE_NEW_DATA,
  API_LOAN_PUSH_TO_YIELDEASY,
  API_LOAN_RECOVER,
  API_LOAN_REQUEST_PRICE,
  API_LOAN_SERVICING_STATUS_CHANGE,
  API_LOAN_SITEXPRO_REPORT,
  API_LOAN_STATISTICS,
  API_LOAN_STATUS_CHANGE,
  API_LOAN_SUB_STATUS_CHANGE,
  API_LOAN_SURVEY,
  API_LOAN_SYSTEM_TAGS,
  API_LOAN_TAG_SUGGESTIONS,
  API_LOAN_TAGS,
  API_LOAN_UPDATE_CLOSING_DATE,
  API_LOAN_UPDATE_FIELDS,
  API_LOAN_UPDATE_LOAN_PROCESS,
  API_LOAN_UPLOAD_MISMO_XML,
  API_LOAN_UPLOAD_WET_SIGNED_TERM_SHEET,
  API_LOAN_VALIDATE_LOAN_NUMBERS,
  API_PIPELINE_BUSINESS_DAYS,
  API_SYNC_NATIONAL_FLOOR,
  pipelineLoanFilterQuery,
} from 'config'
import { AiEngine } from 'config/ai.type'
import { ICheckListData, ILoanSubmissionConditionAiHistoryData } from 'pages/LoanSubmission/types'
import Api from 'services/api'
import { setLoanNumber } from 'utils/setLoanNumber'

import { downloadFile } from './documents'

export const filterPipeline = (filterQuery: pipelineLoanFilterQuery = {}) => {
  return Api.get(API_LOAN_PIPELINE, filterQuery)
}

// --- Saved pipeline views (per user) ---
export interface PipelineView {
  id: number
  name: string
  pipelineType: string
  filters: Record<string, any>
  /** `public` is shared with the whole company. Missing means private. */
  visibility?: 'private' | 'public'
  createdBy?: string
}

export const getPipelineViews = (pipelineType?: string): Promise<PipelineView[]> => {
  return Api.get(API_LOAN_PIPELINE_VIEWS, pipelineType === undefined ? {} : { pipelineType })
}

export const savePipelineView = (data: {
  id?: number
  name: string
  pipelineType: string
  filters: Record<string, any>
  visibility?: 'private' | 'public'
}): Promise<{ success: number; id?: number; message?: string }> => {
  return Api.post(API_LOAN_PIPELINE_VIEWS, data)
}

export const deletePipelineView = (id: number) => {
  return Api.delete(API_LOAN_PIPELINE_VIEW_BY_ID, {}, { id })
}

export const getLoanOverview = (loanNumber?: string | number) => {
  return Api.get(API_LOAN_OVERVIEW, {}, {}, loanNumber ? { loanNumber } : {})
}

export const getduplicateLoans = (
  loanNumber: string,
  filters: { skip?: number; count?: number; includePaidOff?: boolean } = {},
) => {
  return Api.get(
    API_LOAN_GET_DUPLICATE_LOANS,
    {
      skip: filters.skip ?? 0,
      count: filters.count ?? 2,
      // The endpoint leaves paid-off loans out unless asked, so this is what un-hides them.
      includePaidOff: filters.includePaidOff ? 'true' : 'false',
    },
    { loanNumber },
  )
}

export const updateLoanFields = (loanFields: any, isTriggerCondition: boolean = true) => {
  return Api.put(API_LOAN_UPDATE_FIELDS, { loanFields, isTriggerCondition })
}

export const getTierLevelList = (programId?: number) => {
  if (!programId) return Promise.resolve([])
  return Api.get(API_LOAN_GET_TIER_LEVEL_LIST, {}, { programId })
}

export const loanMoveToServicing = (loanNumber: number) => {
  return Api.get(API_LOAN_MOVE_TO_SERVICING, {}, { loanNumber })
}

export const loanStatusChange = (
  status: string,
  loanNumber: number,
  override: boolean = false,
  autoNote: string = '',
) => {
  return Api.get(API_LOAN_STATUS_CHANGE, { override, autoNote }, { status, loanNumber })
}

export const loanSubStatusChange = (
  subStatus: string,
  loanNumber: number,
): Promise<{ success: boolean; message?: string }> => {
  return Api.post(API_LOAN_SUB_STATUS_CHANGE, { subStatus }, { loanNumber })
}

export const servicingStatusChange = (status: string, loanNumber: number) => {
  return Api.get(API_LOAN_SERVICING_STATUS_CHANGE, {}, { status, loanNumber })
}

export const updateLoanProcess = (data: any) => {
  return Api.post(API_LOAN_UPDATE_LOAN_PROCESS, data)
}

export const uploadWetSignedTermSheet = (fileKey: string) => {
  return Api.post(API_LOAN_UPLOAD_WET_SIGNED_TERM_SHEET, { fileKey })
}

export const updateClosingDate = (data: any) => {
  return Api.post(API_LOAN_UPDATE_CLOSING_DATE, data)
}

export const getLoanLogs = (key: string, propertyId: number = -1) => {
  return Api.get(API_LOAN_LOG, {}, { key, propertyId })
}

export const getLoanDetailLog = (field: string, section?: string, recordKey?: string) => {
  const query: Record<string, string> = { field }
  if (section) query.section = section
  if (recordKey) query.recordKey = recordKey
  return Api.get(API_LOAN_DETAIL_LOG, query)
}

export const getLoanProcessLogs = (key: string, loanNumber: number) => {
  if (loanNumber === undefined) loanNumber = 0
  return Api.get(API_LOAN_PROCESS_LOG, {}, { key, loanNumber })
}

export const updateLoanProcessLog = (key: string, loanNumber: number, data: Record<string, any>) => {
  return Api.put(API_LOAN_PROCESS_LOG, data, { key, loanNumber: loanNumber || 0 })
}

export const getLoanHistory = () => {
  return Api.get(API_LOAN_HISTORY)
}

export const loanOverviewSection = (loanNumber?: string | number) => {
  return Api.get(API_LOAN_OVERVIEW_SECTION, {}, {}, loanNumber ? { loanNumber } : {})
}

export const loanOverviewSectionByLoanNumber = (loanNumber: number) => {
  return Api.get(API_LOAN_OVERVIEW_SECTION_BY_LOAN_NUMBER, {}, { loanNumber })
}

export const deleteLoan = (loanNumber: number) => {
  return Api.delete(API_LOAN_DELETE, {}, { loanNumber })
}

export const recoverLoan = (loanNumber: number) => {
  return Api.put(API_LOAN_RECOVER, {}, { loanNumber })
}

export const getLoanStatistics = (
  data: any,
): Promise<{
  byStatus: Array<{ count: string; loanStatus?: string; servicingStatus?: string; sum: string }>
  byLocked?: Array<{ rateLocked: boolean; count: string }>
  brokerUserScore?: string
  quarterScore?: string
  totalCount: number
  closingCount?: number
  closingDays?: any
  totalCalculationCount: number
  productionStats?: any
  myLoans?: boolean
}> => {
  return Api.post(API_LOAN_STATISTICS, data)
}

export const getPipelineBusDays = () => {
  return Api.get(API_PIPELINE_BUSINESS_DAYS)
}

export const setPipelineBusDays = (values: any[]) => {
  return Api.post(API_PIPELINE_BUSINESS_DAYS, { values })
}

export const loanCloneFile = (data: object) => {
  return Api.post(API_LOAN_CLONE_FILE, data)
}

export const getLoanCloneGroup = (loanNumber?: string | number) => {
  return Api.get(API_LOAN_CLONE_GROUP, {}, {}, loanNumber ? { loanNumber } : {})
}

export const unlinkLoanCloneGroup = () => {
  return Api.delete(API_LOAN_CLONE_GROUP)
}

export const getFloodOrder = () => {
  return Api.get(API_FLOOR_ORDER)
}

export const saveFloodOrderInfo = (data: { key: string; value: any; propertyKey?: string }) => {
  return Api.put(API_FLOOR_ORDER, data)
}

export const submitFloodOrder = (data: Record<string, any>) => {
  return Api.post(API_FLOOR_ORDER, data, {}, { timeout: 150000 })
}

export const syncNationalFlood = (data?: { propertyKey?: string }) => {
  return Api.post(API_SYNC_NATIONAL_FLOOR, data || {})
}

export const pushLoanToYieldeasy = (data: { docKeys: string[]; isForce: boolean }) => {
  return Api.post(API_LOAN_PUSH_TO_YIELDEASY, data)
}

export const getLoanToNexysStatus = (
  loanNumber?: number,
): Promise<
  {
    success: boolean
    loans: { index: number; loanNumber: string | number; locked: boolean; sharedFeatures?: Record<string, boolean> }[]
    name: string
    title: string
  }[]
> => {
  return Api.get(API_LOAN_PUSH_TO_NEXYS, {}, {}, loanNumber != null ? { loanNumber } : {})
}

export const pushLoanToNexys = (
  data: {
    to: string
    docKeys: string[]
    loanIndex?: number
    sharedFeatures?: string[]
  },
  loanNumber?: number,
) => {
  return Api.post(API_LOAN_PUSH_TO_NEXYS, data, {}, loanNumber != null ? { loanNumber } : {})
}

export const updatePushLoanToNexysLockStatus = (data: Record<string, any>) => {
  return Api.put(API_LOAN_PUSH_TO_NEXYS_LOCK_STATUS, data)
}

export const getPushLoanToNexysMergeData = () => {
  return Api.get(API_LOAN_PUSH_TO_NEXYS_MERGE_NEW_DATA)
}

export const pushLoanToNexysMergeData = () => {
  return Api.post(API_LOAN_PUSH_TO_NEXYS_MERGE_NEW_DATA)
}

export const getNexysPeers = () => {
  return Api.get(API_LOAN_NEXYS_PEERS)
}

export const getNexysSharedPipeline = (peerId: string, request?: Record<string, any>) => {
  // The servicer answers filters, sort and paging with its own query, so the whole request travels
  // as one JSON param. No request at all still means "the whole shared list", as it used to.
  return Api.get(API_LOAN_NEXYS_SHARED_PIPELINE, request ? { query: JSON.stringify(request) } : {}, { peerId })
}

export const getNexysSharedLoanShell = (peerId: string, loanNumber: number) => {
  return Api.get(API_LOAN_NEXYS_SHARED_SHELL, {}, { peerId, loanNumber })
}

export const getNexysSharedLoanPage = (peerId: string, loanNumber: number, pageKey: string) => {
  return Api.get(API_LOAN_NEXYS_SHARED_PAGE, {}, { peerId, loanNumber, pageKey })
}

export const getNexysSharedDocument = (peerId: string, loanNumber: number, docKey: string) => {
  return Api.get(API_LOAN_NEXYS_SHARED_DOCUMENT, { docKey }, { peerId, loanNumber })
}

export const validateLoanNumbers = (loanNumbers: number[]): Promise<Record<number, boolean>> => {
  return Api.post(API_LOAN_VALIDATE_LOAN_NUMBERS, loanNumbers)
}

export const getZillowRouteByZipCode = (zipCode: string) => {
  return Api.get(API_LOAN_GET_ZILLOW_URL, {}, { zipCode })
}

export const downloadZillowDocumentByZipCode = (zipCode: string) => {
  return Api.post(API_LOAN_GET_ZILLOW_URL, {}, { zipCode })
}

export const submitSurvey = (values: Record<string, any>) => {
  return Api.post(API_LOAN_SURVEY, values)
}

export const filterSurvey = (data: Record<string, any>) => {
  return Api.get(API_LOAN_SURVEY, data)
}

export const getSitexProProperty = (data: Record<string, string>) => {
  return Api.get(API_LOAN_SITEXPRO_REPORT, data)
}

export const getSitexProReport = (data: Record<string, string>) => {
  return Api.post(API_LOAN_SITEXPRO_REPORT, data)
}

export const getGeoLocation = () => {
  return Api.get(API_LOAN_GET_GEOLOCATION)
}

export const fetchFciData = () => {
  return Api.get(API_LOAN_FETCH_FCI_DATA, {}, {}, { timeout: 600000 })
}

export const getBorrowerOutstandingItems = () => {
  return Api.get(API_LOAN_BORROWER_OUTSTANDING_ITEMS)
}

export const downloadEntireAuditLog = async () => {
  const loanNumber = setLoanNumber()
  const res: Blob = await Api.get(
    API_LOAN_DOWNLOAD_ALL_CHANGES,
    {},
    {},
    {
      responseType: 'blob',
    },
  )
  downloadFile(`Loan Audit Log - ${loanNumber}.csv`, res)
}

export const downloadMismoXml = async () => {
  const loanNumber = setLoanNumber()
  const res: Blob = await Api.get(
    API_LOAN_DOWNLOAD_MISMO_XML,
    {},
    {},
    {
      responseType: 'blob',
    },
  )
  downloadFile(`Loan 3.4 XML - ${loanNumber}.xml`, res)
}

export const uploadMismoXml = async (file: File) => {
  return Api.uploadFiles(API_LOAN_UPLOAD_MISMO_XML, {}, {}, [file])
}

/** Same URL as multipart upload; uses PUT + JSON body so the server reads XML from S3 by key. */
export const uploadMismoXmlFromFileKey = async (fileKey: string) => {
  return Api.put(API_LOAN_UPLOAD_MISMO_XML, { fileKey }, {}, { timeout: 0 })
}

export const getMlds = async () => {
  return Api.get(API_LOAN_MLDS)
}

export const updateMlds = async (data: Record<string, any>) => {
  return Api.put(API_LOAN_MLDS, data)
}

export const requestPriceLoan = async () => {
  return Api.post(API_LOAN_REQUEST_PRICE)
}

export const analyzeDocument = async (document: Record<string, any>, override: boolean = false) => {
  return Api.post(API_LOAN_ANALYZE_DOCUMENT, { document, override }, {}, { timeout: 0 })
}

export const extractDocumentBySections = async (fileKey: string, sections: any[], id: number = 0) => {
  return Api.post(API_LOAN_EXTRACT_DOCUMENT_BY_SECTIONS, { fileKey, sections, id }, {}, { timeout: 0 })
}

export const getAnalyzeDocumentHistory = async () => {
  return Api.get(API_LOAN_ANALYZE_DOCUMENT_HISTORY)
}

export const createSampleLoan = (productType: string) => {
  return Api.post(API_LOAN_CREATE_SAMPLE, { productType })
}

export const getAddressRoutes = (
  origin: string,
  destination: string,
): Promise<
  {
    distanceMeters: number // 4139493
    duration: string // '137182s'
    polyline: {
      encodedPolyline: string
    }
  }[]
> => {
  return Api.post(API_LOAN_GET_ADDRESS_ROUTES, { origin, destination })
}

export interface LoanOverviewOverrides {
  loanNumber?: string
  entityName?: string
  borrowerName?: string
  propertyAddress?: string
  loanProgram?: string
  mers?: string
  closingDate?: string
  thirdPartyId?: string
  preferredLanguage?: string
}

export const getLoanOverviewOverrides = (loanNumber?: string | number): Promise<LoanOverviewOverrides> => {
  return Api.get(API_LOAN_OVERVIEW_OVERRIDES, {}, {}, loanNumber ? { loanNumber } : {})
}

export const saveLoanOverviewOverrides = (data: LoanOverviewOverrides): Promise<LoanOverviewOverrides> => {
  return Api.put(API_LOAN_OVERVIEW_OVERRIDES, data)
}

export const getLoanTags = (loanNumber: number | string): Promise<string[]> => {
  return Api.get(API_LOAN_TAGS, {}, { loanNumber })
}

export const updateLoanTags = (loanNumber: number | string, tags: string[]): Promise<string[]> => {
  return Api.put(API_LOAN_TAGS, { tags }, { loanNumber })
}

export const getLoanTagSuggestions = (q = ''): Promise<string[]> => {
  return Api.get(API_LOAN_TAG_SUGGESTIONS, { q })
}

// Protected system tags (e.g. Foreclosure/Bankruptcy/REO) that cannot be removed once applied.
export const getLoanSystemTags = (): Promise<string[]> => {
  return Api.get(API_LOAN_SYSTEM_TAGS)
}

export const getLoanActions = (): Promise<Record<string, any>> => {
  return Api.get(API_LOAN_ACTIONS)
}

// Per-user, per-grid display settings (column order/widths/hidden/sort).
export const getGridConfig = (gridKey: string): Promise<{ success: number; config: Record<string, any> }> => {
  return Api.get(API_LOAN_GRID_CONFIG, {}, { gridKey })
}

export const saveGridConfig = (gridKey: string, config: Record<string, any>) => {
  return Api.put(API_LOAN_GRID_CONFIG, { config }, { gridKey })
}

export const saveLoanAction = (
  actionType: string,
  key: string,
  data: { label?: string; config?: Record<string, any>; removed?: boolean },
): Promise<Record<string, any>> => {
  return Api.put(API_LOAN_ACTION, data, { actionType, key })
}

export const removeLoanAction = (
  actionType: string,
  key: string,
  data: { label?: string; config?: Record<string, any> },
): Promise<Record<string, any>> => {
  return Api.delete(API_LOAN_ACTION, data, { actionType, key })
}

export const getCustomInformation = (menuKey: string): Promise<Record<string, any>> => {
  return Api.get(API_LOAN_CUSTOM_INFORMATION, {}, { menuKey })
}

export const getComplianceConfig = (): Promise<{ success: number; data: Record<string, any> }> => {
  return Api.get(API_LOAN_COMPLIANCE_CONFIG)
}

export const getCompliance = (): Promise<{ success: number; data: Record<string, any> }> => {
  return Api.get(API_LOAN_COMPLIANCE)
}

export const postCompliance = (data: Record<string, any>): Promise<{ success: number; data: Record<string, any> }> => {
  return Api.post(API_LOAN_COMPLIANCE, data)
}

export const updateCustomInformationField = (
  menuKey: string,
  fieldKey: string,
  value: any,
): Promise<Record<string, any>> => {
  return Api.post(API_LOAN_CUSTOM_INFORMATION, { fieldKey, value }, { menuKey })
}

export const startAiReviewCondition = (
  conditionNo: number,
  options: {
    historyId?: number
    fileKey?: string
    model?: string
    engine?: AiEngine
  } = {},
): Promise<{
  success: boolean
  message: string
  data: ILoanSubmissionConditionAiHistoryData[]
  checklist: ICheckListData
  aiNote: string
  responsibility: string
}> => {
  return Api.post(API_LOAN_AI_REVIEW_CONDITION, { ...options }, { conditionNo }, { timeout: 0 })
}

export const getAiReviewCondition = (conditionNo: number) => {
  return Api.get(API_LOAN_AI_REVIEW_CONDITION, {}, { conditionNo })
}

// export const getAiReviewConditionPrompt = (
//   conditionNo: number,
//   fileKey: string,
// ): Promise<{ prompt: string; files: BaseFile[] }> => {
//   return Api.get(API_LOAN_AI_REVIEW_CONDITION_PROMPT, { fileKey }, { conditionNo })
// }

export const updateAiReviewConditionHistory = (
  conditionNo: number,
  data: { historyId: number; historyIdx: number; fileIdx: number; data: Record<string, any> },
) => {
  return Api.put(API_LOAN_AI_REVIEW_CONDITION, data, { conditionNo })
}
